import { useState } from 'react'
import Styles from './Link.module.scss'
import { SearchField } from '../SearchField/SearchField'
import { EmptyState } from './EmptyState'
import { MediaFileThumbnail } from '../MediaFileThumbnail/MediaFileThumbnail'
import { Error } from 'components/ArticlePostPreview/PostPreviewBody/Error'
import { validateImageUrl, ValidationResult } from './validators'
import { useDebouncedCallback } from '@react-hookz/web'
import { useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import {
  ACCEPTED_IMAGE_MIME_TYPES,
  ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
  ACCEPTED_VIDEO_MIME_TYPES,
} from 'screens/Finder/components/views/GalleryView/constants'

export const Link = () => {
  const { setSelectedImages, selectedImages, toggleSelectedImage, accept } = useImagePickerContext()
  const [link, setLink] = useState('')
  const [validationState, setValidationState] = useState<ValidationResult['state']>('empty')

  const withVideo = accept?.includes(ACCEPTED_VIDEO_MIME_TYPES)
  const onlyVideo =
    withVideo && !accept?.includes(ACCEPTED_IMAGE_MIME_TYPES) && !accept?.includes(ACCEPTED_IMAGE_MIME_TYPES_NO_SVG)

  const debouncedValidation = useDebouncedCallback(
    (url: string) => {
      const result = validateImageUrl(url, withVideo, onlyVideo)

      setValidationState(result.state)

      if (result.state === 'valid-image' || result.state === 'valid-base64') {
        setSelectedImages([{ url: result.url }])
      } else {
        setSelectedImages([])
      }
    },
    [setSelectedImages],
    500,
  )

  const handleLinkChange = (newLink: string) => {
    setLink(newLink)
    debouncedValidation(newLink)
  }

  const renderContent = () => {
    switch (validationState) {
      case 'empty':
        return <EmptyState />
      case 'invalid':
        return <Error errorMessages={['Please enter a valid URL']} />
      case 'valid-url':
        return (
          <Error
            errorMessages={[
              'The URL must be a direct link to an image file (ending in .jpg, .png, etc) or a base64 encoded image. For example: https://example.com/image.jpg',
            ]}
          />
        )
      case 'valid-image':
      case 'valid-base64':
        return (
          <MediaFileThumbnail
            mediaFile={{ url: link, source_url: link }}
            onToggleSelect={toggleSelectedImage}
            isSelected={selectedImages.some((i) => i.url === link)}
            controls
          />
        )
      case 'invalid-image-not-allowed':
        return <Error errorMessages={['Cannot upload images. Please enter a valid video URL.']} />
      default:
        return null
    }
  }

  return (
    <div className={Styles.link} data-testid="link-provider">
      <SearchField value={link} onChange={handleLinkChange} placeholder="https://" />
      <div className={Styles.preview}>{renderContent()}</div>
    </div>
  )
}
